
import {
    ApplicationUserModel,
    AuditEscalationGroupModel,
    AuditModel,
    AuditTimingTypeEnum,
    CategoryModel,
    EscalationGroupTypeEnum,
    GroupModel,
    PermissionTypeEnum,
    PlanItemModel,
    ProcessTypeEnum,
    ResponseModel,
    ResponseTypeEnum,
    ShiftModel,
    TenantUserModel,
    ImplementationStateEnum,
    MeasureModel,
} from '@/libs/Api';
import Page from '@/Page.vue';
import { Component, Prop, Ref, Watch } from 'vue-property-decorator';
import { cloneDeep, difference, isEqual } from 'lodash';
import { auditsStore } from '@/libs/audits/+state/store';
import { CrudAction, CrudReponse, CrudResponseAction } from '@/libs/core/+state/models/crud-action';
import { Guid } from '@/libs/common/functions/guid';
import moment from 'moment';
import { planItemsStore } from '@/libs/plan-items/+state/store';
import { applicationUserStore } from '@/libs/user-settings/+state/store';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import { authStore } from '@/libs/auth/+store/store';
import { AuthGetter } from '@/libs/auth/models/auth-state';

import WorkTaskAuditTab from '@/libs/work-tasks/pages/WorkTaskAuditTab.vue';
import WorkTaskFindingTab from '@/libs/work-tasks/pages/WorkTaskFindingTab.vue';
import WorkTaskMeasureTab from '@/libs/work-tasks/pages/WorkTaskMeasureTab.vue';
import { categoriesStore } from '@/libs/categories/+state/store';
import { createCrudQueryPayload } from '@/libs/core/+state/models/crud-query-payload';
import { VDA63 } from '@/audit-settings/VDA63';
import QuestionFileDetail from '@/libs/audits/components/QuestionFileDetail.vue';
import InstructionFilePreview from '@/libs/work-tasks/components/InstructionFilePreview.vue';
import TakeSolvingItem from '@/libs/issues-solving/components/TakeSolvingItem.vue';
import { EventTypeEnum } from '@/libs/core/models/event-type-enum';
import { auditEscalationGroupsStore } from '@/libs/audit-escalation-groups/+state/store';
import { groupsStore } from '@/libs/groups/+state/store';
import { measuresStore } from '@/libs/measures/+state/store';
import WorkTaskMeasureItem from '@/libs/work-tasks/components/WorkTaskMeasureItem.vue';
import { compare, applyPatch, ReplaceOperation } from 'fast-json-patch';
import { workplacesStore } from '@/libs/workplaces/+state/store';
import { WorkplaceModel } from '@/libs/Api';

@Component({
    components: {
        TakeSolvingItem,
        InstructionFilePreview,
        QuestionFileDetail,
        WorkTaskAuditTab,
        WorkTaskFindingTab,
        WorkTaskMeasureTab,
        WorkTaskMeasureItem,
    },
})
export default class WorkTaskAuditForm extends Page {
    /* Page props */
    @Prop() id!: string;
    @Prop() tenantId!: string;

    /* Model variables */
    planItem: PlanItemModel | null = null;
    planItemOriginal: PlanItemModel | null = null;
    audit: AuditModel | null = null;
    category: CategoryModel | undefined = undefined;
    auditEscalationGroups: AuditEscalationGroupModel[] | undefined = undefined;
    day!: string;
    auditId!: string;
    isTaking = false;
    takeItemId: string | null | undefined = null;

    /* Component state variables */
    tab = 0;
    isFormValid = true;
    loading = true;
    saving = false;
    savingSilent = false;
    responseLockDialog = false;
    responseLockDialogIsShutdownMandatory = false;
    responseLockDialogIShutdownEnd: boolean | null = null;
    responseLockDialogIsShutdownOptional = false;
    responseLockExistingEvidenceNumber = false;
    hasChanges = false;
    auditPerformed = false;
    savingInstructionConfirmation = false;
    planItemId: string | null = null;
    mistakeSnackbar = false;
    confirmDialog = false;
    shutdownToDate: string | null = null;
    protected ProcessTypeEnum: typeof ProcessTypeEnum = ProcessTypeEnum;
    // VDA 6.3 setup state (placeholder flow)
    vdaSetupConfirmed = false;
    vdaSetupActive = false;
    vdaProcessStepsCount: string | null = '';
    vdaShownProcessStepsCount = 0;
    get vdaPlanItemProcessSteps() {
        return (this.planItem as any)?.planItemProcessSteps ?? [];
    }
    get vdaPlanItemProductGroups() {
        return (this.planItem as any)?.planItemProductGroups ?? [];
    }

    get vdaContinueDisabled() {
        const planItemAny = this.planItem as any;
        const steps: any[] = Array.isArray(planItemAny?.planItemProcessSteps) ? planItemAny.planItemProcessSteps : [];
        if (steps.length === 0) return true;
        // Validate step names
        const stepNames = steps.map((s) => (s?.processStep?.name ?? '').toString().trim());
        const allNamesFilled = stepNames.every((n) => n.length > 0);
        if (!allNamesFilled) return true;
        // Unique step names (case-insensitive)
        const stepNamesLower = stepNames.map((n) => n.toLowerCase());
        const stepNamesUnique = new Set(stepNamesLower).size === stepNamesLower.length;
        if (!stepNamesUnique) return true;
        // Validate step numbers: numeric, start at 1, strictly increasing by 1, no duplicates
        const nums: number[] = steps.map((s) => Number.parseInt((s?.processStep?.stepNumber ?? '').toString(), 10));
        if (nums.some((n) => Number.isNaN(n) || n < 1)) return true;
        const sorted = [...nums].sort((a, b) => a - b);
        for (let i = 0; i < sorted.length; i++) {
            if (sorted[i] !== i + 1) return true;
        }
        // If product groups are required: at least one group, each with a name and at least one checkbox
        const requireProductGroups = this.audit?.useProductGroups == true;
        if (!requireProductGroups) return false;
        const groups: any[] = Array.isArray(planItemAny?.planItemProductGroups) ? planItemAny.planItemProductGroups : [];
        if (groups.length === 0) return true;
        const links: any[] = Array.isArray(planItemAny?.productGroupProcessSteps) ? planItemAny.productGroupProcessSteps : [];
        const groupNames = groups.map((g) => (g?.productGroup?.name ?? '').toString().trim());
        const groupAllNamesFilled = groupNames.every((n) => n.length > 0);
        if (!groupAllNamesFilled) return true;
        // Unique group names (case-insensitive)
        const groupNamesLower = groupNames.map((n) => n.toLowerCase());
        const groupNamesUnique = new Set(groupNamesLower).size === groupNamesLower.length;
        if (!groupNamesUnique) return true;
        const allGroupsValid = groups.every((g) => {
            const nameOk = (g?.productGroup?.name ?? '').toString().trim().length > 0;
            if (!nameOk) return false;
            const linkCount = links.filter((l) => l?.productGroup?.id === g?.productGroup?.id).length;
            return linkCount > 0;
        });
        return !allGroupsValid;
    }

    /* User state variables */
    applicationUser: ApplicationUserModel = applicationUserStore.useGetter(CrudGetter.Detail) as ApplicationUserModel;
    tenantUser: TenantUserModel = authStore.useGetter(AuthGetter.GetTenantUser) as TenantUserModel;

    /* Others */
    closeAfterSave = false;
    planItemSubscription: any = null;
    supplierWorkplaceLoading = false;
    supplierWorkplaceFilter: string | null = null;

    /* Refs */
    @Ref() auditTab!: WorkTaskAuditTab;

    /* Nové proměnné pro dialog a opatření */
    showMeasuresDialog = false;
    openMeasures: MeasureModel[] = [];

    loadingFinished() {
        const scrollTop = this.getQueryInt('scroll');
        if (scrollTop) {
            this.$nextTick(() => {
                this.setQuery('scroll', null);
                document!.getElementById('layout-content')!.scrollTo({
                    top: scrollTop,
                    behavior: 'auto',
                });
            });
        }
    }

    /* Watches */
    @Watch('planItem', { deep: true })
    planItemChanged() {
        this.checkChanges();
    }

    @Watch('id')
    idChanged(v: string) {
        this.tab = 0;
        this.planItemId = v;
        this.planItem = null;
        this.planItemOriginal = null;
        this.audit = null;
        this.category = undefined;
        this.day = '';
        this.load();
    }

    @Watch('planItem.workplaceId')
    onWorkplaceChanged() {
        this.checkOpenMeasures();
    }

    /* Getters */

    get isLoading() {
        return this.loading || this.auditTab?.loading == true;
    }

    get isValid(): boolean {
        return this.auditTab?.isValid();
    }

    get isMobile(): boolean {
        return this.$vuetify.breakpoint.width < 991;
    }

    get canEditWorkplace() {
        return (
            (this.planItem != null &&
                ((this.planItem.auditTenantUsers?.some((x) => x.tenantUserId == this.tenantUser.id) &&
                    this.category?.processType != ProcessTypeEnum.IssueSolving) ||
                    (this.planItem.tenantUserId == this.tenantUser.id &&
                        this.category?.processType == ProcessTypeEnum.IssueSolving)) &&
                this.planItem.responseLock == false) ||
            this.hasAccessToCategory(this.audit?.categoryId, [PermissionTypeEnum.MistakeCorrection])
        );
    }

    get canEditQuestions() {
        return (
            (this.planItem != null &&
                ((this.planItem.auditTenantUsers?.some((x) => x.tenantUserId == this.tenantUser.id) &&
                    this.category?.processType != ProcessTypeEnum.IssueSolving) ||
                    (this.planItem.tenantUserId == this.tenantUser.id &&
                        this.category?.processType == ProcessTypeEnum.IssueSolving)) &&
                this.planItem.responseLock == false) ||
            this.hasAccessToCategory(this.audit?.categoryId, [PermissionTypeEnum.MistakeCorrection])
        );
    }

    get canEditFiles() {
        return (
            (this.planItem != null &&
                ((this.planItem.auditTenantUsers?.some((x) => x.tenantUserId == this.tenantUser.id) &&
                    this.category?.processType != ProcessTypeEnum.IssueSolving) ||
                    (this.planItem.tenantUserId == this.tenantUser.id &&
                        this.category?.processType == ProcessTypeEnum.IssueSolving)) &&
                this.planItem.responseLock == false) ||
            this.hasAccessToCategory(this.audit?.categoryId, [PermissionTypeEnum.MistakeCorrection])
        );
    }

    get canEditCorrectiveMeasurements() {
        return (
            (this.planItem != null &&
                this.planItem.responseLock == true &&
                this.planItem.fullLock == false &&
                this.hasAccessToCategory(this.audit?.categoryId, [
                    PermissionTypeEnum.Findings,
                    PermissionTypeEnum.FindingManager
                ])) ||
            this.hasAccessToCategory(this.audit?.categoryId, [PermissionTypeEnum.MistakeCorrection])
        );
    }

    get canCorrectMistakes() {
        const hasCorrectQueryParam = this.$route.query?.correct?.toString() == '1';
        return this.hasAccessToCategory(this.audit?.categoryId, [PermissionTypeEnum.MistakeCorrection]) && hasCorrectQueryParam;
    }

    get routeName() {
        return this.$router.currentRoute.name;
    }

    get isRequestedInstructionsConfirmation() {
        return (
            !this.planItem?.responseLock &&
            this.category?.enforceInstuctionsConfirmation &&
            this.planItem?.isIntructionsConfirmed != true
        );
    }

    showInstructions(): boolean {
        return (
            (this.planItem &&
                this.category &&
                this.audit &&
                this.planItem.responseLock == false &&
                this.category.enforceInstuctionsConfirmation == true &&
                this.planItem.isIntructionsConfirmed != true) ??
            false
        );
    }

    shouldShowVdaSetup(): boolean {
        if (!this.planItem || !this.audit) return false;
        if (this.planItem.responseLock) return false;
        if (this.showInstructions()) return false;
        if (this.vdaSetupActive && this.vdaSetupConfirmed != true) return true;
        const isVda = this.audit?.category?.responseType == ResponseTypeEnum.VDA63;
        const useAtPerformance = this.audit?.useVdaDefinitionAtPerformance == true;
        const hasSteps = Array.isArray((this.planItem as any)?.planItemProcessSteps)
            ? (this.planItem as any).planItemProcessSteps.length > 0
            : false;
        return isVda && useAtPerformance && !hasSteps && this.vdaSetupConfirmed != true;
    }

    initializeVdaSetup() {
        // Seed arrays so that v-model bindings exist
        const planItemAny = this.planItem as any;
        if (!Array.isArray(planItemAny.planItemProcessSteps)) this.$set(planItemAny, 'planItemProcessSteps', []);
        if (!Array.isArray(planItemAny.planItemProductGroups)) this.$set(planItemAny, 'planItemProductGroups', []);
        this.vdaShownProcessStepsCount = planItemAny.planItemProcessSteps.length;
        this.vdaSetupActive = true;
    }

    onVdaAddProcessStepClick() {
        if (!this.planItem) return;
        const planItemAny = this.planItem as any;
        if (!Array.isArray(planItemAny.planItemProcessSteps)) this.$set(planItemAny, 'planItemProcessSteps', []);
        const steps = planItemAny.planItemProcessSteps as any[];
        this.vdaSetupActive = true;
        const index = steps.length;
        const guid = Guid.NewGuid();
        steps.push({
            id: Guid.EmptyGuid(),
            processStepId: guid,
            processStep: {
                id: guid,
                key: guid,
                isVirtual: true,
                name: null,
                stepNumber: (index + 1).toString(),
                position: index + 1,
            },
        });
        this.vdaShownProcessStepsCount = steps.length;
    }

    onVdaRemoveProcessStep(index: number) {
        const steps = (this.planItem as any).planItemProcessSteps as any[];
        steps.splice(index, 1);
        steps.forEach((x: any, idx: number) => {
            x.processStep.position = idx + 1;
            x.processStep.stepNumber = (idx + 1).toString();
        });
        this.vdaShownProcessStepsCount = steps.length;
    }

    onVdaAddProductGroupClick() {
        const planItemAny = this.planItem as any;
        if (!Array.isArray(planItemAny.planItemProductGroups)) this.$set(planItemAny, 'planItemProductGroups', []);
        const list = planItemAny.planItemProductGroups as any[];
        const guid = Guid.NewGuid();
        list.push({
            id: Guid.EmptyGuid(),
            productGroupId: guid,
            productGroup: {
                id: guid,
                key: guid,
                isVirtual: true,
                name: null,
            },
        });
    }

    onVdaRemoveProductGroup(index: number) {
        const list = (this.planItem as any).planItemProductGroups as any[];
        const item = list[index];
        list.splice(index, 1);
        // also remove cross links if exist later (kept placeholder, backend may ignore)
        const links = (this.planItem as any).productGroupProcessSteps as any[];
        if (Array.isArray(links)) {
            for (let i = links.length - 1; i >= 0; i--) {
                if (links[i]?.productGroupId === item?.productGroup?.id) links.splice(i, 1);
            }
        }
    }

    hasVdaProductGroupProcessStep(productGroup: any, processStep: any) {
        const links = (this.planItem as any).productGroupProcessSteps as any[];
        if (!Array.isArray(links)) return false;
        return links.some((x) => x.productGroup?.id === productGroup?.id && x.processStep?.id === processStep?.id);
    }

    setVdaProductGroupProcessStepChecked($event: any, productGroup: any, processStep: any) {
        const planItemAny = this.planItem as any;
        if (!Array.isArray(planItemAny.productGroupProcessSteps)) this.$set(planItemAny, 'productGroupProcessSteps', []);
        const links = planItemAny.productGroupProcessSteps as any[];
        const existsIndex = links.findIndex(
            (x) => x.productGroup?.id === productGroup?.id && x.processStep?.id === processStep?.id
        );
        if (existsIndex >= 0 && $event == false) {
            links.splice(existsIndex, 1);
        } else if (existsIndex < 0 && $event == true) {
            links.push({
                id: Guid.EmptyGuid(),
                productGroupId: productGroup!.id,
                processStepId: processStep!.id,
                productGroup: productGroup,
                processStep: processStep,
                position: links.length + 1,
            });
        }
    }

    protected get isShutdownToDateInFuture() {
        return this.shutdownToDate && new Date(this.shutdownToDate) > new Date();
    }

    protected canTake() {
        const groups: GroupModel[] = groupsStore.useGetter(CrudGetter.Data) ?? [];
        const myGroupIds = groups
            .filter((x) => x.groupUsers?.some((n) => n.tenantUserId === this.tenantUser.id))
            .map((x) => x.id!);
        return (
            this.planItem &&
            this.category &&
            this.category.processType == ProcessTypeEnum.IssueSolving &&
            this.planItem.tenantUserId != this.tenantUser.id // &&
            //this.auditEscalationGroups?.some((x) => x.auditId == this.planItem!.auditId && myGroupIds.includes(x.groupId!))
        );
    }

    /* Mounted */
    mounted() {
        this.closeAfterSave = false;
        if (this.id && this.id != Guid.EmptyGuid()) {
            this.planItemId = this.id;
            this.load().then(() => {
                this.checkOpenMeasures();
                this.$nextTick(() => {
                    if (this.shouldShowVdaSetup()) this.initializeVdaSetup();
                    if (this.shouldShowSupplierWorkplaceSelect()) this.ensureSupplierWorkplacesLoaded();
                });
            });
        } else {
            this.planItemId = Guid.EmptyGuid();
            this.auditId = this.$router.currentRoute.params.auditId;
            this.day = moment().format('YYYY-MM-DDT00:00:00');
            this.load().then(() => {
                this.checkOpenMeasures();
                this.$nextTick(() => {
                    if (this.shouldShowVdaSetup()) this.initializeVdaSetup();
                    if (this.shouldShowSupplierWorkplaceSelect()) this.ensureSupplierWorkplacesLoaded();
                });
            });
        }
        this.checkChanges();
        this.planItemSubscription = this.$store.subscribe((mutation, _state) => {
            if (
                mutation.type === planItemsStore.getActionName(CrudResponseAction.PartialUpdateSuccess) ||
                mutation.type === planItemsStore.getActionName(CrudResponseAction.UpdateSuccess)
            ) {
                const planItem = (mutation.payload.item ?? mutation.payload.itemBasic ?? {}) as PlanItemModel;
                if (this.planItem?.id == planItem.id) {
                    this.planItem = {
                        ...this.planItem,
                        ...planItem,
                    };
                }
            }
        });
    }

    unmounted() {
        this.planItemSubscription?.unsubscribe();
    }

    /* Action functions */

    private load() {
        return new Promise((resolve, reject) => {
            this.loading = true;
            this.loadPlanItem()
                .then((e: PlanItemModel) => {
                    if (e.auditId) this.auditId = e.auditId;
                    this.loadAudit()
                        .then((e: AuditModel) => {
                            this.loading = false;
                        })
                        .catch((e: any) => {
                            reject(e);
                        });
                })
                .catch((e: any) => {
                    reject(e);
                });
        });
    }

    private loadPlanItem(): any {
        return new Promise((resolve, reject) => {
            if (this.planItemId == Guid.EmptyGuid()) {
                auditsStore.dispatch(CrudAction.Get, { id: this.auditId });
                this.subscribe(auditsStore, CrudReponse.Get)
                    .then((e: AuditModel) => {
                        let stations: string[] = [];
                        try {
                            if (this.getQuery('stations')) {
                                stations = JSON.parse(this.getQuery('stations') ?? '[]');
                            }
                        } catch (e) {
                            console.log(e);
                        }
                        this.planItem = {
                            id: Guid.EmptyGuid(),
                            tenantUserId: this.tenantUser.id,
                            auditId: this.auditId,
                            audit: e,
                            time: moment().format('YYYY-MM-DDTHH:mm:ss'),
                            responses: [],
                            responseLock: false,
                            fullLock: false,
                            isIntructionsConfirmed: false,
                            instructionsConfirmedOn: null,
                            isReleased: null,
                            processType: e.processType,
                            auditTimingType: AuditTimingTypeEnum.ByUser,
                            auditTenantUsers: [
                                {
                                    tenantUserId: this.tenantUser.id,
                                },
                            ],
                            shiftId: this.getQuery('shiftId') ?? null,
                            workplaceId: this.getQuery('workplaceId') ?? null,
                            stationId: stations.length == 1 ? stations[0] : null,
                            planItemCustomFieldGroups: [],
                        } as PlanItemModel;
                        this.planItemOriginal = cloneDeep(this.planItem);
                        this.loadCategory(e.categoryId!)
                            .then((c: CategoryModel) => {
                                this.loadEscalationGroups(this.auditId).then(() => {
                                    this.hasChanges = false;
                                    resolve(this.planItem);
                                });
                            })
                            .catch((e) => {
                                reject(e);
                            });
                    })
                    .catch((e) => {
                        reject(e);
                    });
            } else {
                this.$nextTick(() => {
                    const planItemId = this.planItemId;
                    planItemsStore.dispatch(CrudAction.Get, { id: planItemId });
                    this.subscribe(planItemsStore, CrudReponse.Get)
                        .then((e: PlanItemModel) => {
                            this.planItem = { ...e };
                            this.planItemOriginal = cloneDeep(this.planItem);
                            this.loadCategory(e.audit!.categoryId!)
                                .then((c: CategoryModel) => {
                                    this.loadEscalationGroups(this.planItem!.auditId!).then(() => {
                                        this.category = c;
                                        this.hasChanges = false;
                                        this.checkOpenMeasures();
                                        resolve(e);
                                    });
                                })
                                .catch((e) => {
                                    reject(e);
                                });
                        })
                        .catch((e: any) => {
                            reject(e);
                        });
                });
            }
        });
    }

    private loadAudit(): Promise<any> {
        return new Promise((resolve, reject) => {
            auditsStore.dispatch(CrudAction.Get, { id: this.auditId!, key: this.auditId! });
            this.subscribe(auditsStore, CrudReponse.Get, this.auditId!)
                .then((e: AuditModel) => {
                    this.audit = e;
                    resolve(e);
                })
                .catch((e: any) => {
                    reject(e);
                });
        });
    }

    loadCategory(categoryId: string): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            categoriesStore.dispatch(CrudAction.Get, { id: categoryId });
            this.subscribe(categoriesStore, CrudReponse.Get)
                .then((e: CategoryModel) => {
                    this.category = e;
                    this.checkOpenMeasures();
                    resolve(e);
                })
                .catch((e: any) => {
                    reject(e);
                });
        });
    }

    loadEscalationGroups(auditId: string): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            auditEscalationGroupsStore.dispatch(
                CrudAction.GetAll,
                createCrudQueryPayload<AuditEscalationGroupModel>(undefined, [
                    { field: 'auditId', op: 'eq', comparand: auditId },
                    { field: 'escalationGroupType', op: 'eq', comparand: EscalationGroupTypeEnum.IssueEscalation },
                ])
            );
            this.subscribe(auditEscalationGroupsStore, CrudReponse.GetAll)
                .then((e: AuditEscalationGroupModel[]) => {
                    this.auditEscalationGroups = e;
                    resolve(e);
                })
                .catch((e: any) => {
                    reject(e);
                });
        });
    }

    private verifyEvidenceNumber(): Promise<boolean> {
        return new Promise((resolve, reject) => {
            if (this.planItem == null || this.planItem!.evidenceNumber == null || this.planItem!.evidenceNumber.length == 0) {
                resolve(false);
            } else {
                planItemsStore.dispatch(
                    CrudAction.GetAll,
                    createCrudQueryPayload<PlanItemModel>(
                        undefined,
                        [
                            { field: 'evidenceNumber', op: 'eq', comparand: this.planItem!.evidenceNumber! },
                            { field: 'id', op: 'ne', comparand: this.planItem!.id ?? Guid.EmptyGuid() },
                        ],
                        undefined,
                        false,
                        'VerifyEvidenceNumber'
                    )
                );
                this.subscribe(planItemsStore, CrudReponse.GetAll, 'VerifyEvidenceNumber')
                    .then((e: PlanItemModel[]) => {
                        resolve(e.length > 0);
                    })
                    .catch(() => {
                        resolve(false);
                    });
            }
        });
    }

    private save(showResponseLock: true) {
        const scroll = Math.round(document!.getElementById('layout-content')!.scrollTop);
        this.responseLockDialog = false;
        this.confirmDialog = false;
        if (this.auditTab?.validate()) {
            const notFinishedResponses: Array<ResponseModel> = this.auditTab.getNotFinishedResponses();
            if (this.audit?.isRequiredSavingAllInputs && notFinishedResponses.length > 0) {
                this.mistakeSnackbar = true;
                return;
            }
            let validQuestions = true;
            if (this.audit?.category?.responseType == ResponseTypeEnum.VDA63) {
                validQuestions = this.validateVda63Questions();
            }
            if (
                this.canEditQuestions &&
                !this.planItem!.responseLock &&
                this.planItem!.isRequestedResponseLock != true &&
                showResponseLock &&
                validQuestions &&
                notFinishedResponses.length == 0
            ) {
                this.savingSilent = true;
                this.verifyEvidenceNumber().then((e: boolean) => {
                    if (this.category!.responseType == ResponseTypeEnum.ProductAudit) {
                        const isAllOk =
                            this.auditTab.getResponses().filter((x) => {
                                return x.responseParts!.filter((n) => n.isOk == false).length > 0;
                            }).length == 0;
                        if (isAllOk) {
                            this.planItem!.isReleased = true;
                        } else {
                            this.planItem!.isReleased = null;
                        }
                    }
                    this.savingSilent = false;
                    if (this.planItem!.isWorkplaceShutdown) {
                        this.shutdownToDate = moment().format('YYYY-MM-DDTHH:mm:ss');
                    } else {
                        this.shutdownToDate = null;
                    }
                    this.responseLockDialogIsShutdownOptional = this.auditTab
                        .getResponses()
                        .some((x) => x.findings!.length > 0);
                    if (this.planItem!.isWorkplaceShutdown && this.planItem!.shutdownToDate != null) {
                        this.responseLockDialogIShutdownEnd = true;
                        this.shutdownToDate = this.planItem!.shutdownToDate;
                    } else {
                        this.responseLockDialogIShutdownEnd = null;
                    }
                    this.responseLockDialogIsShutdownMandatory = this.auditTab
                        .getResponses()
                        .some((x) => x.findings!.length == 0);
                    this.responseLockDialog = true;
                    this.responseLockExistingEvidenceNumber = e;
                });
            } else {
                const hasCorrectQueryParam = this.$route.query?.correct?.toString() == '1';
                const isCorrection = this.hasAccessToCategory(this.audit?.categoryId, [PermissionTypeEnum.MistakeCorrection]) && hasCorrectQueryParam;
                if (notFinishedResponses.length > 0) {
                    this.planItem!.isRequestedResponseLock = false;
                }
                if (
                    this.planItem!.isRequestedResponseLock &&
                    this.category!.processType != ProcessTypeEnum.IssueSolving &&
                    !isCorrection
                ) {
                    this.planItem!.time = moment().format('YYYY-MM-DDTHH:mm:ss');
                }
                if (this.planItem && this.planItem.performations && this.planItem.performations.length > 0) {
                    this.planItem.performations.forEach((x) => {
                        if (x.toDate == null) {
                            x.toDate = moment().format('YYYY-MM-DDTHH:mm:ss');
                        }
                    });
                }
                this.saving = true;
                const responses: Array<ResponseModel> = this.auditTab.getResponses(isCorrection);
                const planItem = { ...this.planItem } as PlanItemModel;
                planItem.shutdownToDate =
                    this.responseLockDialogIsShutdownMandatory ||
                    (this.responseLockDialogIsShutdownOptional && this.responseLockDialogIShutdownEnd)
                        ? this.shutdownToDate
                        : null;
                planItem.responses = responses;
                if (!planItem.responseLock) {
                    planItem.tenantUserId = this.tenantUser.id!;
                }
                if (this.audit!.usePlanItemFindingManagerList) {
                    planItem.findingManagers = this.auditTab.getPlanItemFindingManagers();
                }
                if (this.planItemId == Guid.EmptyGuid()) {
                    planItemsStore.dispatch(CrudAction.Create, { item: planItem });
                    this.subscribe(planItemsStore, CrudReponse.Create)
                        .then((e: PlanItemModel) => {
                            this.planItemId = e.id!;
                            this.auditPerformed = true;
                            this.planItem = e;
                            this.planItemOriginal = cloneDeep(this.planItem);
                            this.saving = false;
                            if (this.closeAfterSave) this.$router.go(-1);
                            if (e.responseLock == true) {
                                if (this.category!.processType == ProcessTypeEnum.Checklist && e.shiftId != null) {
                                    this.$router.replace({
                                        name: 'ShiftWorkDetail',
                                        params: { tenantId: this.$router.currentRoute.params.tenantId, id: e.shiftId },
                                    });
                                } else if (this.category!.processType == ProcessTypeEnum.IssueSolving) {
                                    this.$router.replace({
                                        name: 'IssuesSolving',
                                        params: { tenantId: this.$router.currentRoute.params.tenantId },
                                    });
                                } else {
                                    this.$router.replace({
                                        name: 'WorkTaskAuditTab',
                                        params: { tenantId: this.$router.currentRoute.params.tenantId, id: e.id! },
                                        query: { scroll: scroll.toString() },
                                    });
                                }
                            } else {
                                this.$router.replace({
                                    name: 'WorkTaskAuditFormExisting',
                                    params: { tenantId: this.$router.currentRoute.params.tenantId, id: e.id! },
                                    query: { scroll: scroll.toString() },
                                });
                            }
                        })
                        .catch((e) => {
                            this.saving = false;
                            throw e;
                        });
                } else {
                    if (isCorrection) {
                        planItemsStore.dispatch(CrudAction.Update, {
                            item: planItem,
                            headers: {
                                Action: 'CorrectMistakes',
                            },
                        });
                    } else {
                        planItemsStore.dispatch(CrudAction.Update, { item: planItem });
                    }
                    this.subscribe(planItemsStore, CrudReponse.Update)
                        .then((e: PlanItemModel) => {
                            this.auditPerformed = true;
                            this.planItem = e;
                            this.planItemOriginal = cloneDeep(this.planItem);
                            this.saving = false;
                            if (this.closeAfterSave) this.$router.go(-1);
                            if (e.responseLock == true) {
                                if (this.category!.processType == ProcessTypeEnum.Checklist && e.shiftId != null) {
                                    this.$router.replace({
                                        name: 'ShiftWorkDetail',
                                        params: { tenantId: this.$router.currentRoute.params.tenantId, id: e.shiftId },
                                    });
                                } else if (this.category!.processType == ProcessTypeEnum.IssueSolving) {
                                    this.$router.replace({
                                        name: 'IssuesSolving',
                                        params: { tenantId: this.$router.currentRoute.params.tenantId },
                                    });
                                } else {
                                    this.$router.replace({
                                        name: 'WorkTaskAuditTab',
                                        params: { tenantId: this.$router.currentRoute.params.tenantId, id: e.id! },
                                        query: { scroll: scroll.toString() },
                                    });
                                }
                            } else {
                                this.$nextTick(() => {
                                    this.auditTab.refresh();
                                    this.setQuery('scroll', scroll.toString());
                                });
                            }
                        })
                        .catch((e) => {
                            this.saving = false;
                            throw e;
                        });
                }
            }
        } else {
            this.mistakeSnackbar = true;
        }
    }

    validateVda63Questions(): boolean {
        const responses = this.auditTab.getResponses();
        for (const processElement of VDA63.ProcessElementsSteps) {
            if (responses.any((x) => x.question?.questionGroup?.processElement == processElement.Element)) {
                if (processElement.Validation.ResponseCount != null) {
                    if (
                        responses.count(
                            (x) =>
                                x.question?.questionGroup?.processElement == processElement.Element &&
                                x.scoreValue != null &&
                                x.scoreValue != -1
                        ) < processElement.Validation.ResponseCount!
                    ) {
                        return false;
                    }
                }
                if (processElement.Validation.ProductResponseCount != null) {
                    if (
                        responses.count(
                            (x) =>
                                x.question?.questionGroup?.processElement == processElement.Element &&
                                x.scoreValue != null &&
                                x.scoreValue != -1 &&
                                x.processStep?.isHidden == true &&
                                x.processStep.stepNumber == 'Product'
                        ) < processElement.Validation.ProductResponseCount!
                    ) {
                        return false;
                    }
                }
                if (processElement.Validation.ProcessResponseCount != null) {
                    if (
                        responses.count(
                            (x) =>
                                x.question?.questionGroup?.processElement == processElement.Element &&
                                x.scoreValue != null &&
                                x.scoreValue != -1 &&
                                x.processStep?.isHidden == true &&
                                x.processStep.stepNumber == 'Process'
                        ) < processElement.Validation.ProcessResponseCount!
                    ) {
                        return false;
                    }
                }
                if (processElement.Validation.ProcessStepResponseCount != null) {
                    if (
                        responses
                            .where(
                                (x) =>
                                    x.question?.questionGroup?.processElement == processElement.Element &&
                                    x.scoreValue != null &&
                                    x.scoreValue != -1 &&
                                    this.planItem!.planItemProcessSteps!.select((n) => n.processStep!).any(
                                        (n) => n.id == x.processStepId && n.isHidden != true
                                    )
                            )
                            .groupBy((x) => x.questionId!)
                            .count() < processElement.Validation.ProcessStepResponseCount!
                    ) {
                        return false;
                    }
                }
            }
        }
        return true;
    }

    canEditAnyImplementationOfCorrectiveMeasurements() {
        if (this.planItem == null || this.planItem.responses == null) return false;
        return this.planItem.responses.any((x) => this.canEditImplementationOfCorrectiveMeasurements(x));
    }

    canEditImplementationOfCorrectiveMeasurements(response: ResponseModel) {
        return (
            (this.planItem != null &&
                this.planItem.responseLock == true &&
                this.planItem.fullLock == false &&
                this.hasAccessToCategory(this.audit?.categoryId, [
                    PermissionTypeEnum.Measures,
                    PermissionTypeEnum.MeasuresAllUsers
                ]) &&
                response.implementerUserId == this.tenantUser.id!) ||
            this.hasAccessToCategory(this.audit?.categoryId, [PermissionTypeEnum.MistakeCorrection])
        );
    }

    checkChanges() {
        this.hasChanges = !isEqual(this.planItemOriginal, this.planItem) || this.auditTab?.hasChanges();
    }

    public plainToHtml(value: string) {
        return value?.replaceAll('\n', '<br />');
    }

    confirmInstructions() {
        if (this.planItem!.id != null && this.planItem!.id != Guid.EmptyGuid()) {
            const item = { ...this.planItem };
            item.isIntructionsConfirmed = true;
            item.instructionsConfirmedOn = moment().format('YYYY-MM-DDTHH:mm:ss');
            this.savingInstructionConfirmation = true;
            planItemsStore.dispatch(CrudAction.Update, { item: item });
            this.subscribe(planItemsStore, CrudReponse.Update)
                .then((e: PlanItemModel) => {
                    this.savingInstructionConfirmation = false;
                    this.planItem!.isIntructionsConfirmed = item.isIntructionsConfirmed;
                    this.planItem!.instructionsConfirmedOn = item.instructionsConfirmedOn;
                    this.$nextTick(() => {
                        document.getElementById('layout-content')!.scrollTo({ top: 0, behavior: 'smooth' });
                    });
                })
                .catch((e) => {
                    this.savingInstructionConfirmation = false;
                    throw e;
                });
        } else {
            this.planItem!.isIntructionsConfirmed = true;
            this.planItem!.instructionsConfirmedOn = moment().format('YYYY-MM-DDTHH:mm:ss');
            this.$nextTick(() => {
                document.getElementById('layout-content')!.scrollTo({ top: 0, behavior: 'smooth' });
            });
        }
    }

    onGoToVdaAuditSetup() {
        if (!this.planItem) return;
        const isNew = this.planItem.id == null || this.planItem.id == Guid.EmptyGuid();
        if (isNew) {
            this.vdaSetupConfirmed = true;
            this.$nextTick(() => {
                document.getElementById('layout-content')!.scrollTo({ top: 0, behavior: 'smooth' });
            });
            return;
        }

        const planItem = { ...this.planItem } as PlanItemModel;
        this.savingSilent = true;
        planItemsStore.dispatch(CrudAction.Update, { item: planItem });
        this.subscribe(planItemsStore, CrudReponse.Update)
            .then((e: PlanItemModel) => {
                this.savingSilent = false;
                this.planItem = e;
                this.planItemOriginal = cloneDeep(this.planItem);
                this.vdaSetupConfirmed = true;
                this.$nextTick(() => {
                    document.getElementById('layout-content')!.scrollTo({ top: 0, behavior: 'smooth' });
                });
            })
            .catch((err) => {
                this.savingSilent = false;
                throw err;
            });
    }

    shouldShowSupplierWorkplaceSelect(): boolean {
        if (!this.planItem || !this.audit || !this.category) return false;
        if (this.planItem.responseLock) return false;
        if (this.showInstructions()) return false;
        if (this.shouldShowVdaSetup()) return false;
        const isAuditProcess = this.category.processType == ProcessTypeEnum.Audit;
        const isSupplierAudit = this.category.isSupplierAudit == true;
        const hasWorkplace = (this.planItem.workplaceId ?? null) != null;
        return isAuditProcess && isSupplierAudit && !hasWorkplace;
    }

    get filteredSupplierWorkplaces(): WorkplaceModel[] {
        try {
            let items = [...((workplacesStore.useGetter(CrudGetter.Data) as WorkplaceModel[]) ?? [])];
            items.sort((a: WorkplaceModel, b: WorkplaceModel) => {
                const an = (a.name ?? '').toString();
                const bn = (b.name ?? '').toString();
                return an.localeCompare(bn);
            });
            const filter = (this.supplierWorkplaceFilter || '')
                .toLowerCase()
                .normalize('NFD')
                .replace(/\p{Diacritic}/gu, '');
            if (filter && filter.length > 0) {
                items = items.filter((x) => {
                    const name = (x.name ?? '')
                        .toLowerCase()
                        .normalize('NFD')
                        .replace(/\p{Diacritic}/gu, '');
                    const location = (x.location?.name ?? '')
                        .toLowerCase()
                        .normalize('NFD')
                        .replace(/\p{Diacritic}/gu, '');
                    return name.includes(filter) || location.includes(filter);
                });
            }
            return items;
        } catch (e) {
            return [] as WorkplaceModel[];
        }
    }

    ensureSupplierWorkplacesLoaded(): Promise<any> {
        return new Promise((resolve, reject) => {
            try {
                const existing = (workplacesStore.useGetter(CrudGetter.Data) as WorkplaceModel[]) ?? [];
                if (existing.length > 0) {
                    resolve(existing);
                    return;
                }
            } catch (e) {
                // ignore
            }
            this.supplierWorkplaceLoading = true;
            workplacesStore.dispatch(CrudAction.GetAll, createCrudQueryPayload({ field: 'name', index: 1, order: 'asc' }));
            this.subscribe(workplacesStore, CrudReponse.GetAll)
                .then((e: WorkplaceModel[]) => {
                    this.supplierWorkplaceLoading = false;
                    resolve(e);
                })
                .catch((err: any) => {
                    this.supplierWorkplaceLoading = false;
                    reject(err);
                });
        });
    }

    selectSupplierWorkplace(workplaceId: string | undefined) {
        if (!workplaceId || !this.planItem) return;
        this.$set(this.planItem, 'workplaceId', workplaceId);
        this.$nextTick(() => {
            document.getElementById('layout-content')!.scrollTo({ top: 0, behavior: 'smooth' });
        });
    }

    closeOnMobile() {
        if (this.hasChanges) this.confirmDialog = true;
        else this.$router.go(-1);
    }

    onShutdownFromDateAddMinutes(minutes: number) {
        if (!this.shutdownToDate) {
            this.shutdownToDate = moment().format('YYYY-MM-DDTHH:mm:ss');
        }
        this.shutdownToDate = moment(this.shutdownToDate, 'YYYY-MM-DDTHH:mm:ss')
            .add(minutes, 'minutes')
            .format('YYYY-MM-DDTHH:mm:ss');
    }

    couldBeDateAddMinutesInFuture(minutes: number) {
        if (!this.shutdownToDate) {
            return true;
        }
        return new Date(this.shutdownToDate).getTime() + minutes * 60 * 1000 > new Date().getTime();
    }

    onTakeClick() {
        const isSharedMode: boolean = authStore.useGetter(AuthGetter.GetSharedMode);
        if (isSharedMode) {
            this.setQuery('take', this.planItem!.id!);
            (window as any).Journaly.publish(EventTypeEnum.QuickLoginRequested);
        } else {
            this.takeItemId = this.planItem!.id;
        }
    }

    onTake() {
        this.load();
        this.auditTab.refresh();
    }

    // Nová metoda pro kontrolu otevřených opatření
    async checkOpenMeasures() {
        if (!this.planItem || !this.planItem.workplaceId) {
            this.openMeasures = [];
            this.showMeasuresDialog = false;
            return;
        }
        if (!this.category?.showNotClosedMeasuresInAudit) {
            return;
        }
        // Načtení otevřených opatření pro dané pracoviště
        const filters = [
            // Backend filtruje podle navázaných entit, ale pro jistotu filtrujeme i na klientu
            { field: 'finding.response.planItem.workplaceId', op: 'eq', comparand: this.planItem.workplaceId },
            {
                field: 'implementationState',
                op: 'in',
                comparand: [ImplementationStateEnum.New, ImplementationStateEnum.InProgress],
            },
        ];
        await measuresStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload<MeasureModel>({ field: 'createdAt', order: 'desc', index: 1 }, filters)
        );
        const allMeasures = measuresStore.useGetter(CrudGetter.Data) as MeasureModel[];
        // Filtrovat pouze opatření na daném pracovišti a otevřená
        const openMeasures = allMeasures.filter(
            (m) =>
                m.finding?.response?.planItem?.workplaceId === this.planItem!.workplaceId &&
                (m.implementationState === ImplementationStateEnum.New ||
                    m.implementationState === ImplementationStateEnum.InProgress)
        );
        if (openMeasures.length > 5) {
            this.openMeasures = openMeasures.slice(0, 5);
        }
        this.openMeasures = openMeasures;
        this.showMeasuresDialog = this.openMeasures.length > 0;
    }
}
